import { useEffect, useMemo, useState } from 'react';
import { TIMEZONES, formatUtcInTimezone } from '../../lib/time.js';
import { api, ApiError } from '../../api/index.js';
import { useToast } from '../ui/toast.jsx';
import { ChangePasswordModal } from '../auth/change-password-modal.jsx';

function useTickingNow() {
  const [now, setNow] = useState(() => new Date().toISOString());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date().toISOString()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

export function ProfileTab({ currentUser, onUserUpdated }) {
  const toast = useToast();
  const [displayName, setDisplayName] = useState(currentUser?.displayName || '');
  const [timezone, setTimezone] = useState(currentUser?.timezone || 'UTC');
  const [busy, setBusy] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const now = useTickingNow();

  useEffect(() => {
    setDisplayName(currentUser?.displayName || '');
    setTimezone(currentUser?.timezone || 'UTC');
  }, [currentUser?.displayName, currentUser?.timezone]);

  const dirty = useMemo(() => {
    return (
      (displayName || '') !== (currentUser?.displayName || '') ||
      timezone !== (currentUser?.timezone || 'UTC')
    );
  }, [displayName, timezone, currentUser]);

  const tooLong = displayName.length > 128;
  const canSave = dirty && !tooLong && !busy;

  async function handleSave() {
    if (!canSave) return;
    setBusy(true);
    try {
      const { user } = await api.users.update(currentUser.id, {
        displayName: displayName.trim() || null,
        timezone,
      });
      onUserUpdated?.(user);
      toast.success('Profile updated.');
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.payload?.error || err.message : 'Update failed.',
      );
    } finally {
      setBusy(false);
    }
  }

  function handleReset() {
    setDisplayName(currentUser?.displayName || '');
    setTimezone(currentUser?.timezone || 'UTC');
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <header className="flex items-center justify-between border-b border-line px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent-soft text-accent">
              <i className="fa-solid fa-id-card-clip" aria-hidden="true" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-ink">Profile</h2>
              <p className="text-sm text-mist">How your account is shown across TMS ADMS.</p>
            </div>
          </div>
        </header>

        <div className="grid grid-cols-1 gap-6 px-6 py-6 lg:grid-cols-2">
          {/* Read-only identity */}
          <ReadOnlyField label="Login" value={`@${currentUser.login}`} icon="fa-at" />
          <ReadOnlyField
            label="Role"
            value={currentUser.role === 'admin' ? 'Administrator' : 'User'}
            icon="fa-user-shield"
          />

          {/* Editable */}
          <div>
            <label htmlFor="profile-displayname" className="field-label">Display name</label>
            <input
              id="profile-displayname"
              className="field"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Optional"
              maxLength={128}
              disabled={busy}
            />
            {tooLong && <p className="mt-1 text-xs text-rose-600">Display name must be at most 128 characters.</p>}
          </div>

          <div>
            <label htmlFor="profile-timezone" className="field-label">
              Timezone
            </label>
            <select
              id="profile-timezone"
              className="field"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              disabled={busy}
            >
              {TIMEZONES.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <p className="mt-1.5 flex items-center gap-1.5 text-xs text-mist">
              <i className="fa-regular fa-clock" aria-hidden="true" />
              Currently {formatUtcInTimezone(now, timezone)}
            </p>
          </div>
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-line bg-canvas px-6 py-4">
          <button
            type="button"
            onClick={handleReset}
            disabled={!dirty || busy}
            className="btn-secondary"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="btn-primary"
          >
            {busy ? (
              <>
                <span className="loader-spinner" aria-hidden="true" /> Saving…
              </>
            ) : (
              <>
                <i className="fa-solid fa-floppy-disk" aria-hidden="true" /> Save changes
              </>
            )}
          </button>
        </footer>
      </div>

      {/* Security card */}
      <div className="card">
        <header className="flex items-center justify-between border-b border-line px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent-soft text-accent">
              <i className="fa-solid fa-shield-halved" aria-hidden="true" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-ink">Security</h2>
              <p className="text-sm text-mist">Account security and password management.</p>
            </div>
          </div>
        </header>
        <div className="px-6 py-6">
          <button type="button" onClick={() => setPwOpen(true)} className="btn-secondary">
            <i className="fa-solid fa-key" aria-hidden="true" /> Change password
          </button>
          <p className="mt-2 text-xs text-mist">
            Changing your password signs out every other session for your account immediately.
          </p>
        </div>
      </div>

      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
    </div>
  );
}

function ReadOnlyField({ label, value, icon }) {
  return (
    <div>
      <div className="field-label">{label}</div>
      <div className="flex items-center gap-3 rounded-2xl border border-line bg-canvas px-4 py-3 text-sm text-ink">
        {icon && <i className={`fa-solid ${icon} text-mist`} aria-hidden="true" />}
        <span className="truncate">{value}</span>
      </div>
    </div>
  );
}
